// src/pages/Axiom.jsx
// Axiom Intelligence Platform — Command Center
// Nobody has built the intelligence layer for gigawatt AI factory development.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Brain, Building2, CheckCircle2,
  Clock, Database, Zap, Thermometer, Users, Server,
  Radio, Activity, X, Eye, BarChart3, AlertCircle,
  TrendingUp, Shield, Network, Landmark, ArrowRight,
  RefreshCw, ChevronRight, Layers, Lock, Sparkles,
  Factory, Globe, Cpu,
} from 'lucide-react';

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS & PALETTE
// ════════════════════════════════════════════════════════════════════════════

const ZONE_STATUS = {
  CRITICAL:  {
    label: 'CRITICAL', dot: 'bg-red-500', border: 'border-red-500/60',
    glow: 'shadow-[0_0_24px_rgba(239,68,68,0.35)]', text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300 border border-red-500/30',
    ring: 'ring-2 ring-red-500/40', headerBg: 'bg-red-500/10',
  },
  WARNING:   {
    label: 'WARNING', dot: 'bg-amber-500', border: 'border-amber-500/50',
    glow: 'shadow-[0_0_16px_rgba(245,158,11,0.25)]', text: 'text-amber-400',
    badge: 'bg-amber-500/15 text-amber-300 border border-amber-500/25',
    ring: 'ring-2 ring-amber-500/30', headerBg: 'bg-amber-500/10',
  },
  NOMINAL:   {
    label: 'NOMINAL', dot: 'bg-emerald-500', border: 'border-slate-600/70',
    glow: '', text: 'text-emerald-400',
    badge: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20',
    ring: '', headerBg: 'bg-slate-700/30',
  },
  ANALYZING: {
    label: 'ANALYZING', dot: 'bg-violet-500', border: 'border-violet-500/50',
    glow: 'shadow-[0_0_16px_rgba(139,92,246,0.25)]', text: 'text-violet-400',
    badge: 'bg-violet-500/15 text-violet-300 border border-violet-500/25',
    ring: 'ring-2 ring-violet-500/30', headerBg: 'bg-violet-500/10',
  },
};

const FLOW_COLORS = {
  power:   { stroke: '#f59e0b', label: 'POWER' },
  cooling: { stroke: '#3b82f6', label: 'COOLING' },
  network: { stroke: '#22d3ee', label: 'NETWORK' },
  ot:      { stroke: '#a78bfa', label: 'OT' },
  data:    { stroke: '#6366f1', label: 'DATA' },
};

// ════════════════════════════════════════════════════════════════════════════
// STAKEHOLDER LENSES  — same corpus, different policy view
// ════════════════════════════════════════════════════════════════════════════

const STAKEHOLDER_LENSES = {
  owners_rep: {
    label: "Owner's Rep",
    icon: Landmark,
    activeCls: 'bg-blue-500 text-white shadow-blue-500/30 shadow-md',
    briefingHead: '⬆ Top Domino identified.',
    briefingBody: 'Thermal event in Hall B cascades in 18 min. Cooling reroute to C-03 is safe — 40% spare capacity confirmed. All other signals non-blocking.',
    briefingMeta: "Lens: Owner's Rep · Site + Build + Commission",
  },
  developer: {
    label: 'Developer',
    icon: Building2,
    activeCls: 'bg-emerald-500 text-white shadow-emerald-500/30 shadow-md',
    briefingHead: 'Row 14 delay affects Hall C gate.',
    briefingBody: 'Titan Systems 3 days behind. Penalty clause §12.4 eligible. Cooling event does not impact build schedule if resolved within current window.',
    briefingMeta: 'Lens: Developer · Build phase · 6 domains active',
  },
  fund: {
    label: 'Fund',
    icon: BarChart3,
    activeCls: 'bg-amber-500 text-white shadow-amber-500/30 shadow-md',
    briefingHead: '$320M gate opens early.',
    briefingBody: 'Grid OTA confirmed 11 days early — accelerates commissioning. Hall B thermal creates insurance clause 4.2 exposure if unresolved within window.',
    briefingMeta: 'Lens: Infrastructure Fund · Capital + Exit · 4 domains',
  },
  hyperscaler: {
    label: 'Hyperscaler',
    icon: Server,
    activeCls: 'bg-cyan-500 text-white shadow-cyan-500/30 shadow-md',
    briefingHead: 'SLA risk in Hall B — 18 min window.',
    briefingBody: '4,800 GPUs face thermal throttle. Halls A and C fully nominal. Grid OTA confirms April 15 delivery — commission schedule intact.',
    briefingMeta: 'Lens: Hyperscaler · Commission + SLA · 5 domains',
  },
  lender: {
    label: 'Lender',
    icon: Shield,
    activeCls: 'bg-violet-500 text-white shadow-violet-500/30 shadow-md',
    briefingHead: 'Insurance clause 4.2 at risk.',
    briefingBody: 'Cooling event in Hall B triggers clause review if unresolved. All other risk signals within policy bounds. Capital milestone gates unaffected.',
    briefingMeta: 'Lens: Lender + Insurer · Risk + Capital · 5 domains',
  },
};

// ════════════════════════════════════════════════════════════════════════════
// POLICY DOMAINS — 16 across site → build → commission → capital → exit
// ════════════════════════════════════════════════════════════════════════════

const POLICY_DOMAINS = [
  { phase: 'Site',       id: 'site_selection',     name: 'Site Selection',         status: 'NOMINAL',   count: 0 },
  { phase: 'Site',       id: 'power_water_ota',    name: 'Power + Water OTAs',     status: 'WARNING',   count: 1 },
  { phase: 'Site',       id: 'grid_connection',    name: 'Grid Connection',        status: 'NOMINAL',   count: 0 },
  { phase: 'Build',      id: 'integrator_mgmt',    name: 'Integrator Mgmt',        status: 'WARNING',   count: 1 },
  { phase: 'Build',      id: 'rack_stack',          name: 'Rack / Stack / Turn-Up', status: 'WARNING',   count: 1 },
  { phase: 'Build',      id: 'ppe_compliance',     name: 'PPE Compliance',         status: 'WARNING',   count: 1 },
  { phase: 'Build',      id: 'zone_safety',        name: 'Zone Safety',            status: 'NOMINAL',   count: 0 },
  { phase: 'Commission', id: 'commissioning',      name: 'Commissioning',          status: 'NOMINAL',   count: 0 },
  { phase: 'Commission', id: 'cooling_validation', name: 'Cooling Validation',     status: 'CRITICAL',  count: 1 },
  { phase: 'Commission', id: 'power_validation',   name: 'Power Validation',       status: 'NOMINAL',   count: 0 },
  { phase: 'Capital',    id: 'capital_deployment', name: 'Capital Deployment',     status: 'NOMINAL',   count: 0 },
  { phase: 'Capital',    id: 'vendor_payment',     name: 'Vendor Payment',         status: 'NOMINAL',   count: 0 },
  { phase: 'Capital',    id: 'supply_chain',       name: 'Supply Chain Risk',      status: 'NOMINAL',   count: 0 },
  { phase: 'Exit',       id: 'exit_timing',        name: 'Exit Timing',            status: 'NOMINAL',   count: 0 },
  { phase: 'Exit',       id: 'asset_transfer',     name: 'Asset Transfer',         status: 'NOMINAL',   count: 0 },
  { phase: 'Exit',       id: 'valuation_readiness',name: 'Valuation Readiness',    status: 'NOMINAL',   count: 0 },
];

// ════════════════════════════════════════════════════════════════════════════
// SIGNALS — priority-ranked queue, top domino first
// ════════════════════════════════════════════════════════════════════════════

const INITIAL_SIGNALS = [
  {
    id: 'sig-cooling',
    priority: 1,
    type: 'CRITICAL',
    policyId: 'cooling_validation',
    zoneId: 'cooling',
    zoneLabel: 'Cooling Plant · Hall B',
    title: 'Thermal Anomaly — Chiller C-04 Pre-Failure',
    impact: 'Chiller C-04 showing pre-failure signature. 4,800 GPUs face thermal throttle in 18 min. SLA breach + insurance clause 4.2 activation.',
    cascade: [
      'GPU thermal throttle — 40% performance degradation across Hall B',
      'SLA clause 3.1 breach — hyperscaler notification required within 4 hrs',
      'Insurance clause 4.2 — $2.1M exposure window opens immediately',
    ],
    corpusInsight: 'Corpus matched this exact pre-failure signature in 2 prior incidents across 3 campuses. C-03 has 40% spare capacity. Average resolution: 4 min. Zero GPU loss in both prior cases.',
    recommendation: 'Reroute cooling loop to Chiller C-03 via valve V-12A. Dispatch maintenance to C-04 for inspection. Update cooling validation policy log and notify hyperscaler SLA team.',
    actionMeta: { risk: 'Low', eta: '4 min', policy: 'commission.cooling_validation' },
    etaSeconds: 18 * 60,
    IconComponent: Thermometer,
    resolves: ['cooling'],
  },
  {
    id: 'sig-ppe',
    priority: 2,
    type: 'WARNING',
    policyId: 'ppe_compliance',
    zoneId: 'construction',
    zoneLabel: 'Construction · Sector 7',
    title: 'PPE Zone Violation — Nexus Build Sector 7',
    impact: '2 workers without hard hats in restricted zone. 3rd violation this week. OSHA reporting threshold activates at incident #5 this month.',
    cascade: [
      'OSHA violation count — mandatory report required at incident #5',
      'Contractor penalty clause — Nexus Build §7.2 breach eligible',
      'Insurance safety record — incident log updated for underwriting review',
    ],
    corpusInsight: 'Nexus Build has highest PPE violation rate across 4 tracked campuses. Average contractor response: 12 min. Penalty clause activated at 5 incidents — currently at 3.',
    recommendation: 'Issue immediate zone alert to Sector 7 supervisor. Log violation #3 against Nexus Build contract. Notify owner\'s rep of pattern. Schedule mandatory safety briefing by EOD.',
    actionMeta: { risk: 'Medium', eta: '12 min', policy: 'build.ppe_compliance' },
    etaSeconds: null,
    IconComponent: AlertTriangle,
    resolves: ['construction'],
  },
  {
    id: 'sig-grid',
    priority: 3,
    type: 'INFO',
    policyId: 'power_water_ota',
    zoneId: 'power',
    zoneLabel: 'Power Substation',
    title: 'Grid OTA Milestone — 200MW Delivery Confirmed Early',
    impact: 'Utility confirms April 15 delivery — 11 days ahead of schedule. Commission window can accelerate. Capital deployment gate opens.',
    cascade: [],
    corpusInsight: 'Early delivery pattern matches 2 prior campuses in corpus. Accelerated commissioning adds avg 8% IRR improvement per fund analysis. Recommend updating all stakeholder milestone gates.',
    recommendation: 'Notify hyperscaler of updated availability window. Advance commissioning gate milestones. Update lender and fund stakeholder views with revised schedule.',
    actionMeta: { risk: 'None', eta: 'Immediate', policy: 'site.power_water_ota' },
    etaSeconds: null,
    IconComponent: Zap,
    resolves: [],
  },
  {
    id: 'sig-rack',
    priority: 4,
    type: 'WARNING',
    policyId: 'rack_stack',
    zoneId: 'compute_c',
    zoneLabel: 'Compute Hall C · Row 14',
    title: 'Rack Turn-Up Delay — Titan Systems Row 14',
    impact: 'Titan Systems 3 days behind. Affects Hall C commissioning gate. Penalty clause §12.4 eligible per contract — $180K/day.',
    cascade: [
      'Hall C commissioning delayed minimum 3 days',
      'Titan Systems penalty clause §12.4 — $180K/day exposure',
    ],
    corpusInsight: 'Titan Systems has 2 prior delay incidents in corpus. Typical recovery: 2–4 days with PM escalation. Same PM who resolved Austin-1 delay is available.',
    recommendation: 'Escalate to Titan Systems executive PM. Issue formal delay notice per §12.4. Assess parallel row work opportunities to recover schedule.',
    actionMeta: { risk: 'Medium', eta: '2 days', policy: 'build.rack_stack' },
    etaSeconds: null,
    IconComponent: Building2,
    resolves: ['compute_c'],
  },
];

// ════════════════════════════════════════════════════════════════════════════
// CAMPUS TOPOLOGY BUILDER
// ════════════════════════════════════════════════════════════════════════════

const buildCampusTopology = (resolvedZones = new Set()) => {
  const zs = (id, def) => resolvedZones.has(id) ? 'NOMINAL' : def;
  const sc = (id) => resolvedZones.has(id);

  const nodes = [
    {
      id: 'axiom_cloud',
      type: 'axiomCloud',
      position: { x: 295, y: 10 },
      data: {
        label: 'Axiom Intelligence',
        sublabel: 'Governed Intelligence Layer · 16 Policies · 3 Campuses',
        signalCount: INITIAL_SIGNALS.length,
      },
    },
    {
      id: 'power',
      type: 'campusZone',
      position: { x: 10, y: 180 },
      data: {
        emoji: '⚡', label: 'Power Substation', sub: '200MW · Grid OTA Active',
        status: zs('power', 'WARNING'), metric: '200', metricUnit: 'MW',
        signals: 1, flow: 'power',
      },
    },
    {
      id: 'cooling',
      type: 'campusZone',
      position: { x: 200, y: 180 },
      data: {
        emoji: '❄️', label: 'Cooling Plant',
        sub: sc('cooling') ? 'C-03 Active · Stable' : 'C-04 PRE-FAILURE · 18 MIN',
        status: zs('cooling', 'CRITICAL'),
        metric: sc('cooling') ? '100' : 'FAULT', metricUnit: sc('cooling') ? '%' : '',
        signals: sc('cooling') ? 0 : 1, flow: 'cooling',
        critical: !sc('cooling'),
      },
    },
    {
      id: 'network_core',
      type: 'campusZone',
      position: { x: 510, y: 180 },
      data: {
        emoji: '🌐', label: 'Network Core', sub: '400Gbps · OOB + In-Band',
        status: 'NOMINAL', metric: '2', metricUnit: 'ms',
        signals: 0, flow: 'network',
      },
    },
    {
      id: 'generator',
      type: 'campusZone',
      position: { x: 700, y: 180 },
      data: {
        emoji: '🔋', label: 'Generator Farm', sub: '48MW Diesel · Full Fuel',
        status: 'NOMINAL', metric: '100', metricUnit: '%',
        signals: 0, flow: 'power',
      },
    },
    {
      id: 'compute_a',
      type: 'campusZone',
      position: { x: 10, y: 360 },
      data: {
        emoji: '🖥', label: 'Compute Hall A', sub: '4,800 GPUs · Rows 1–12 Complete',
        status: 'NOMINAL', metric: '4.8K', metricUnit: 'GPU',
        signals: 0, flow: 'compute',
      },
    },
    {
      id: 'compute_b',
      type: 'campusZone',
      position: { x: 215, y: 360 },
      data: {
        emoji: '🖥', label: 'Compute Hall B',
        sub: sc('cooling') ? '4,800 GPUs · Cooling Stable' : 'C-2 THERMAL EVENT',
        status: zs('cooling', 'CRITICAL'),
        metric: '4.8K', metricUnit: 'GPU',
        signals: sc('cooling') ? 0 : 1, flow: 'compute',
        critical: !sc('cooling'),
      },
    },
    {
      id: 'compute_c',
      type: 'campusZone',
      position: { x: 420, y: 360 },
      data: {
        emoji: '🖥', label: 'Compute Hall C', sub: 'Row 14 Delay · 3 Days',
        status: zs('compute_c', 'WARNING'), metric: '9.6K', metricUnit: 'GPU Cap',
        signals: sc('compute_c') ? 0 : 1, flow: 'compute',
      },
    },
    {
      id: 'construction',
      type: 'campusZone',
      position: { x: 640, y: 360 },
      data: {
        emoji: '🏗', label: 'Construction', sub: 'Sector 7 · PPE Violation',
        status: zs('construction', 'WARNING'), metric: '24', metricUnit: 'Workers',
        signals: sc('construction') ? 0 : 1, flow: 'ot',
      },
    },
    {
      id: 'water',
      type: 'campusZone',
      position: { x: 10, y: 520 },
      data: {
        emoji: '💧', label: 'Water Treatment', sub: '2.4M gal/day · Nominal',
        status: 'NOMINAL', metric: '2.4M', metricUnit: 'Gal/d',
        signals: 0, flow: 'cooling',
      },
    },
    {
      id: 'oob',
      type: 'campusZone',
      position: { x: 700, y: 520 },
      data: {
        emoji: '📡', label: 'OOB Network', sub: 'BMC · iDRAC · iLO · All Online',
        status: 'NOMINAL', metric: '847', metricUnit: 'Assets',
        signals: 0, flow: 'network',
      },
    },
  ];

  const mkEdge = (id, source, target, flowType, opts = {}) => ({
    id, source, target,
    type: 'campusFlow',
    animated: true,
    data: { flowType, ...opts },
    style: { stroke: FLOW_COLORS[flowType]?.stroke || '#64748b', strokeWidth: 1.5 },
  });

  const edges = [
    // Cloud → zones (data/governance)
    mkEdge('e-cl-pw', 'axiom_cloud', 'power', 'data'),
    mkEdge('e-cl-co', 'axiom_cloud', 'cooling', 'data'),
    mkEdge('e-cl-net', 'axiom_cloud', 'network_core', 'data'),
    mkEdge('e-cl-con', 'axiom_cloud', 'construction', 'data'),
    // Power flows
    mkEdge('e-pw-a', 'power', 'compute_a', 'power'),
    mkEdge('e-pw-b', 'power', 'compute_b', 'power'),
    mkEdge('e-pw-c', 'power', 'compute_c', 'power'),
    mkEdge('e-gen-c', 'generator', 'compute_c', 'power'),
    // Cooling flows
    mkEdge('e-co-a', 'cooling', 'compute_a', 'cooling', { critical: !sc('cooling') }),
    mkEdge('e-co-b', 'cooling', 'compute_b', 'cooling', { critical: !sc('cooling') }),
    mkEdge('e-wat-co', 'water', 'cooling', 'cooling'),
    // Network flows
    mkEdge('e-net-a', 'network_core', 'compute_a', 'network'),
    mkEdge('e-net-b', 'network_core', 'compute_b', 'network'),
    mkEdge('e-net-c', 'network_core', 'compute_c', 'network'),
    mkEdge('e-net-oob', 'network_core', 'oob', 'network'),
    // OT monitoring
    mkEdge('e-oob-a', 'oob', 'compute_a', 'ot'),
    mkEdge('e-oob-b', 'oob', 'compute_b', 'ot'),
    mkEdge('e-oob-c', 'oob', 'compute_c', 'ot'),
  ];

  return { nodes, edges };
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM REACTFLOW NODE — Axiom Cloud Hub
// ════════════════════════════════════════════════════════════════════════════

const AxiomCloudNode = ({ data }) => (
  <div className="relative">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-indigo-400 !border-2 !border-slate-900" />
    {/* Outer glow */}
    <div className="absolute -inset-3 bg-gradient-to-br from-indigo-500/20 via-violet-500/15 to-fuchsia-500/20 rounded-3xl blur-xl" />
    <div className="relative bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900 border border-indigo-400/40 rounded-2xl px-6 py-4 min-w-[240px] shadow-2xl">
      {/* Live pulse */}
      <div className="absolute -top-1.5 -right-1.5">
        <span className="absolute inline-flex h-4 w-4 rounded-full bg-emerald-400 opacity-60 animate-ping" />
        <span className="relative inline-flex h-4 w-4 rounded-full bg-emerald-400" />
      </div>

      {/* Brain icon */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 bg-gradient-to-br from-indigo-500/30 to-violet-600/30 rounded-xl flex items-center justify-center border border-indigo-400/30">
          <Brain className="w-6 h-6 text-indigo-300" />
        </div>
        <div>
          <div className="text-sm font-bold text-white tracking-wide">AXIOM</div>
          <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Intelligence Platform</div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="flex gap-4 text-center pt-2 border-t border-slate-700/60">
        <div>
          <div className="text-base font-bold text-indigo-300">16</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-wider">Policies</div>
        </div>
        <div>
          <div className="text-base font-bold text-violet-300">127K</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-wider">Events</div>
        </div>
        <div>
          <div className="text-base font-bold text-fuchsia-300">3</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-wider">Campuses</div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
        <span className="text-[9px] text-emerald-400 font-medium tracking-widest uppercase">Governance Active</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM REACTFLOW NODE — Campus Zone
// ════════════════════════════════════════════════════════════════════════════

const CampusZoneNode = ({ data, selected }) => {
  const s = ZONE_STATUS[data.status] || ZONE_STATUS.NOMINAL;
  const isCrit = data.status === 'CRITICAL';

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top}    className="!w-2.5 !h-2.5 !bg-slate-500 !border-2 !border-slate-900" />
      <Handle type="target" position={Position.Left}   className="!w-2.5 !h-2.5 !bg-slate-500 !border-2 !border-slate-900" />
      <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-slate-500 !border-2 !border-slate-900" />
      <Handle type="source" position={Position.Right}  className="!w-2.5 !h-2.5 !bg-slate-500 !border-2 !border-slate-900" />

      {/* Critical pulse ring */}
      {isCrit && (
        <div className="absolute -inset-1 rounded-xl border-2 border-red-500/50 animate-ping opacity-30 pointer-events-none" />
      )}

      <div className={`
        relative bg-gradient-to-br from-slate-800 to-slate-900
        border ${s.border} rounded-xl px-3.5 py-3 min-w-[160px]
        ${s.glow} ${selected ? 'ring-2 ring-white/30' : s.ring}
        transition-all duration-200 group-hover:scale-[1.03]
      `}>

        {/* Status dot */}
        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${s.dot} border-2 border-slate-900`}>
          {isCrit && <span className={`absolute inset-0 rounded-full ${s.dot} animate-ping opacity-75`} />}
        </div>

        {/* Signal badge */}
        {data.signals > 0 && (
          <div className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${isCrit ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'}`}>
            {data.signals}
          </div>
        )}

        {/* Header */}
        <div className={`flex items-center gap-2 mb-2 -mx-1 px-1 py-1 rounded-lg ${s.headerBg}`}>
          <span className="text-base">{data.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className={`text-[11px] font-bold truncate ${isCrit ? 'text-red-300' : 'text-white'}`}>{data.label}</div>
          </div>
        </div>

        {/* Subtitle */}
        <div className={`text-[9px] font-mono mb-2 leading-tight ${isCrit ? 'text-red-400/80' : 'text-slate-400'}`}>
          {data.sub}
        </div>

        {/* Metric */}
        <div className="flex items-baseline gap-1">
          <span className={`text-lg font-bold font-mono ${s.text}`}>{data.metric}</span>
          <span className="text-[9px] text-slate-500 uppercase">{data.metricUnit}</span>
        </div>

        {/* Flow type tag */}
        <div className="mt-1.5">
          <span className="text-[8px] font-mono font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-500">
            {data.flow?.toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM EDGE — animated infrastructure flow
// ════════════════════════════════════════════════════════════════════════════

const CampusFlowEdge = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition, data,
}) => {
  const [edgePath] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const color = data?.critical ? '#ef4444' : (FLOW_COLORS[data?.flowType]?.stroke || '#64748b');

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={{ stroke: color, strokeWidth: data?.critical ? 2 : 1.5, opacity: 0.7 }} />
      {/* Animated moving dot */}
      <circle r="3" fill={color} opacity="0.9">
        <animateMotion dur={data?.critical ? '1.2s' : '2.5s'} repeatCount="indefinite" path={edgePath} />
      </circle>
    </>
  );
};

// Node & edge type registries (must be defined outside component)
const NODE_TYPES = { axiomCloud: AxiomCloudNode, campusZone: CampusZoneNode };
const EDGE_TYPES = { campusFlow: CampusFlowEdge };

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, signals, lens, campusName }) => {
  if (!isOpen) return null;
  const critical = signals.filter(s => s.type === 'CRITICAL').length;
  const warning  = signals.filter(s => s.type === 'WARNING').length;
  const top = signals[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose} />

      <motion.div
        className="relative bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900 border border-slate-600/60 rounded-3xl w-[560px] max-w-[92vw] shadow-[0_40px_120px_rgba(0,0,0,0.7)] overflow-hidden"
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
      >
        {/* Top accent bar */}
        <div className={`h-1 w-full ${critical > 0 ? 'bg-gradient-to-r from-red-500 via-orange-500 to-amber-500' : 'bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500'}`} />

        {/* Header */}
        <div className="px-7 py-5 border-b border-slate-700/60 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${critical > 0 ? 'bg-red-500/20 border border-red-500/30' : 'bg-indigo-500/20 border border-indigo-500/30'}`}>
            {critical > 0 ? '⚡' : '🧠'}
          </div>
          <div>
            <div className="text-white font-bold text-lg tracking-tight">Shift Briefing</div>
            <div className="text-slate-400 text-xs mt-0.5">Axiom Intelligence · {campusName} · {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
          </div>
          <button onClick={onClose} className="ml-auto p-2 rounded-xl text-slate-500 hover:text-white hover:bg-slate-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-slate-700/60 border-b border-slate-700/60">
          {[
            { num: 19200, label: 'GPUs Monitored', color: 'text-indigo-300' },
            { num: critical, label: 'Critical Signals', color: critical > 0 ? 'text-red-400' : 'text-emerald-400' },
            { num: warning,  label: 'Warnings', color: warning > 0 ? 'text-amber-400' : 'text-emerald-400' },
          ].map(({ num, label, color }) => (
            <div key={label} className="py-4 text-center">
              <div className={`text-2xl font-bold font-mono ${color}`}>{num.toLocaleString()}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">{label}</div>
            </div>
          ))}
        </div>

        {/* AI Analysis */}
        <div className="px-7 py-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 bg-indigo-500/20 rounded-lg flex items-center justify-center border border-indigo-500/30">
              <Brain className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.15em]">AI Analysis</span>
          </div>
          <div className="bg-slate-700/30 border border-slate-600/40 rounded-xl p-4 text-sm text-slate-200 leading-relaxed">
            {critical > 0
              ? <><span className="text-white font-semibold">Attention needed:</span> Multiple signals detected. Top priority is a <span className="text-red-300 font-semibold">thermal anomaly in Compute Hall B</span> — chiller C-04 showing pre-failure signature with a <span className="text-red-300 font-semibold">18-minute window</span> before GPU throttle. Corpus matched this pattern in 2 prior incidents. Resolution is known and low-risk.</>
              : <><span className="text-white font-semibold">All clear:</span> Campus operating within policy bounds. 3 advisory signals in queue, none requiring immediate action.</>
            }
          </div>
        </div>

        {/* Detected signals */}
        <div className="px-7 pb-5">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-3">Detected Signals</div>
          <div className="space-y-2">
            {signals.slice(0, 3).map((sig) => {
              const s = ZONE_STATUS[sig.type] || ZONE_STATUS.NOMINAL;
              return (
                <div key={sig.id} className="flex items-center gap-3 bg-slate-700/25 rounded-xl px-4 py-2.5">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`}>
                    {sig.type === 'CRITICAL' && <span className={`absolute w-2 h-2 rounded-full ${s.dot} animate-ping`} />}
                  </div>
                  <span className="text-sm text-slate-200 flex-1">{sig.title}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${s.badge}`}>{sig.type}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-slate-700/60 flex items-center justify-between bg-slate-800/40">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-md flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="text-xs text-slate-400">Powered by <span className="text-indigo-400 font-semibold">Axiom Intelligence</span></span>
          </div>
          <motion.button
            onClick={onClose}
            className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all ${critical > 0 ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white' : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white'}`}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          >
            {critical > 0 ? '⬆ Review Top Domino →' : 'Enter Command Center →'}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE DOMINO ALERT — floats left, like AISentry's ProactiveAlert
// ════════════════════════════════════════════════════════════════════════════

const ProactiveDominoAlert = ({ signal, onViewDetails }) => {
  if (!signal || signal.type !== 'CRITICAL') return null;
  return (
    <motion.div
      className="absolute left-5 top-[30%] z-30"
      initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.8, type: 'spring', stiffness: 280, damping: 24 }}
    >
      <div className="bg-gradient-to-r from-red-900/95 to-rose-950/95 backdrop-blur-xl border border-red-500/50 rounded-2xl shadow-[0_8px_40px_rgba(239,68,68,0.3)] p-4 max-w-[300px]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/25 rounded-xl flex items-center justify-center flex-shrink-0 border border-red-500/30">
            <span className="text-xl">🚨</span>
          </div>
          <div>
            <div className="text-red-300 font-bold text-sm mb-0.5">Top Domino Detected</div>
            <div className="text-white text-xs font-medium leading-snug">{signal.title}</div>
            <div className="text-red-300/60 text-[10px] mt-1">{signal.zoneLabel}</div>
            <button
              onClick={onViewDetails}
              className="mt-2.5 px-3 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-bold rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEFT PANEL — Policy domains + AI briefing + corpus health
// ════════════════════════════════════════════════════════════════════════════

const LeftPanel = ({ lens, policyDomains, resolvedPolicies }) => {
  const phases = ['Site', 'Build', 'Commission', 'Capital', 'Exit'];
  const lensData = STAKEHOLDER_LENSES[lens];

  return (
    <div className="w-56 flex-shrink-0 bg-gradient-to-b from-slate-900 to-slate-950 border-r border-slate-700/50 flex flex-col overflow-hidden">

      {/* AI Briefing card */}
      <div className="p-3 border-b border-slate-700/50">
        <div className="bg-gradient-to-br from-indigo-500/10 to-violet-500/8 border border-indigo-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
            <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-[0.15em]">AI Analysis</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={lens}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <p className="text-[11px] text-slate-200 leading-relaxed">
                <span className="text-white font-semibold">{lensData.briefingHead}</span>{' '}
                {lensData.briefingBody}
              </p>
              <p className="text-[9px] text-slate-500 mt-2 font-mono">{lensData.briefingMeta}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Policy domains */}
      <div className="flex-1 overflow-y-auto p-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded">
        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-2">Policy Domains</div>
        {phases.map(phase => {
          const domains = policyDomains.filter(d => d.phase === phase);
          return (
            <div key={phase} className="mb-3">
              <div className="text-[9px] font-semibold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-slate-600" />
                {phase}
              </div>
              {domains.map(d => {
                const resolved = resolvedPolicies.has(d.id);
                const status = resolved ? 'NOMINAL' : d.status;
                const s = ZONE_STATUS[status];
                return (
                  <div key={d.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg mb-0.5 cursor-pointer transition-all hover:bg-slate-800/60 ${status === 'CRITICAL' ? 'bg-red-500/6 border border-red-500/15' : ''}`}>
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot} ${status === 'CRITICAL' && !resolved ? 'animate-pulse' : ''}`} />
                    <span className={`text-[10px] flex-1 ${status === 'CRITICAL' ? 'text-red-300' : 'text-slate-400'}`}>{d.name}</span>
                    {d.count > 0 && !resolved && (
                      <span className={`text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${status === 'CRITICAL' ? 'bg-red-500/25 text-red-300' : 'bg-amber-500/20 text-amber-400'}`}>{d.count}</span>
                    )}
                    {resolved && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Corpus health */}
      <div className="p-3 border-t border-slate-700/50">
        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-2">Corpus Health</div>
        <div className="bg-slate-800/60 rounded-xl p-2.5">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[10px] text-slate-400">Institutional Memory</span>
            <span className="text-[10px] font-bold text-emerald-400">84%</span>
          </div>
          <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500"
              initial={{ width: 0 }} animate={{ width: '84%' }} transition={{ duration: 1.2, delay: 0.5 }} />
          </div>
          <div className="text-[9px] text-slate-600 mt-1.5 font-mono">127K events · 3 campuses · 16 policies</div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// COUNTDOWN HOOK
// ════════════════════════════════════════════════════════════════════════════

const useCountdown = (initialSeconds, active) => {
  const [remaining, setRemaining] = useState(initialSeconds);
  useEffect(() => {
    if (!active || !initialSeconds) return;
    setRemaining(initialSeconds);
    const interval = setInterval(() => setRemaining(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(interval);
  }, [active, initialSeconds]);
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return { remaining, formatted: `${m}:${s.toString().padStart(2, '0')}` };
};

// ════════════════════════════════════════════════════════════════════════════
// RIGHT PANEL — Top Domino + Signal Queue
// ════════════════════════════════════════════════════════════════════════════

const RightPanel = ({ signals, onApprove, onDelegate, onSnooze, onPromote }) => {
  const [approving, setApproving] = useState(false);
  const topSignal = signals[0];
  const queue = signals.slice(1);
  const countdown = useCountdown(topSignal?.etaSeconds, !!topSignal?.etaSeconds && !approving);

  const handleApprove = async () => {
    setApproving(true);
    await new Promise(r => setTimeout(r, 1400));
    onApprove(topSignal);
    setApproving(false);
  };

  const urgencyColor = topSignal?.etaSeconds
    ? (countdown.remaining < 5 * 60 ? 'text-red-400' : countdown.remaining < 10 * 60 ? 'text-amber-400' : 'text-slate-400')
    : 'text-slate-500';

  const topStatus = ZONE_STATUS[topSignal?.type] || ZONE_STATUS.NOMINAL;

  return (
    <div className="w-[360px] flex-shrink-0 bg-gradient-to-b from-slate-900 to-slate-950 border-l border-slate-700/50 flex flex-col overflow-hidden">

      {/* ── TOP DOMINO ── */}
      <div className="flex-shrink-0 border-b border-slate-700/50">
        {/* Accent bar */}
        <div className={`h-0.5 ${topSignal?.type === 'CRITICAL' ? 'bg-gradient-to-r from-red-500 via-orange-500 to-amber-500' : topSignal?.type === 'WARNING' ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : 'bg-gradient-to-r from-indigo-500 to-violet-500'}`} />

        <div className="p-4">
          {/* Header row */}
          <div className="flex items-center gap-2 mb-3">
            <motion.div
              className={`text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-md border ${topStatus.badge}`}
              animate={{ opacity: topSignal?.type === 'CRITICAL' ? [1, 0.6, 1] : 1 }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ⬆ TOP DOMINO
            </motion.div>
            {topSignal?.etaSeconds && (
              <div className={`ml-auto font-mono text-sm font-bold ${urgencyColor} flex items-center gap-1`}>
                <Clock className="w-3 h-3" />
                {approving ? '✓ Executing…' : countdown.formatted}
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            {topSignal && (
              <motion.div key={topSignal.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22 }}
              >
                {/* Title */}
                <div className={`text-sm font-bold leading-snug border-l-2 pl-2.5 mb-1 ${topSignal.type === 'CRITICAL' ? 'border-red-500 text-red-200' : topSignal.type === 'WARNING' ? 'border-amber-500 text-amber-100' : 'border-indigo-500 text-indigo-100'}`}>
                  {topSignal.title}
                </div>

                {/* Zone tag */}
                <div className="text-[10px] text-slate-500 font-mono ml-[15px] mb-3">
                  📍 {topSignal.zoneLabel} · {topSignal.policyId}
                </div>

                {/* Impact */}
                <p className="text-[11px] text-slate-300 leading-relaxed mb-3">{topSignal.impact}</p>

                {/* Cascade */}
                {topSignal.cascade.length > 0 && (
                  <>
                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                      Cascade if ignored
                      <div className="flex-1 h-px bg-slate-700/80" />
                    </div>
                    <div className="space-y-1 mb-3">
                      {topSignal.cascade.map((c, i) => (
                        <div key={i} className="flex gap-2 text-[10px] text-slate-400">
                          <span className={`flex-shrink-0 font-bold ${topSignal.type === 'CRITICAL' ? 'text-red-500' : 'text-amber-500'}`}>↳</span>
                          {c}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Corpus insight */}
                <div className="bg-indigo-500/8 border border-indigo-500/20 rounded-xl px-3 py-2.5 mb-3 flex gap-2">
                  <Brain className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-slate-300 leading-relaxed">{topSignal.corpusInsight}</p>
                </div>

                {/* Recommendation */}
                <div className="bg-slate-800/60 border border-slate-600/40 rounded-xl p-3 mb-3">
                  <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Recommended Action</div>
                  <p className="text-[11px] text-slate-200 leading-relaxed mb-2">{topSignal.recommendation}</p>
                  <div className="flex gap-3 text-[9px] font-mono text-slate-500">
                    <span>Risk: <span className="text-emerald-400 font-bold">{topSignal.actionMeta.risk}</span></span>
                    <span>ETA: <span className="text-cyan-400 font-bold">{topSignal.actionMeta.eta}</span></span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <motion.button
                    onClick={handleApprove}
                    disabled={approving}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-700 text-black font-bold text-xs py-2.5 rounded-xl transition-all flex items-center justify-center gap-1.5"
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  >
                    {approving ? <><RefreshCw className="w-3 h-3 animate-spin" /> Executing…</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Approve & Execute</>}
                  </motion.button>
                  <button onClick={() => onDelegate(topSignal)} className="px-3 py-2.5 text-xs text-slate-300 bg-slate-700/60 hover:bg-slate-600/60 border border-slate-600/50 rounded-xl transition-all">Delegate</button>
                  <button onClick={() => onSnooze(topSignal)} className="px-3 py-2.5 text-xs text-slate-500 border border-slate-700/50 rounded-xl hover:border-slate-600 transition-all">Snooze</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── SIGNAL QUEUE ── */}
      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded">
        <div className="p-3 pb-2">
          <div className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em]">
            Next in Queue — {queue.length} signal{queue.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="px-3 pb-3 space-y-2">
          {queue.map((sig, i) => {
            const s = ZONE_STATUS[sig.type] || ZONE_STATUS.NOMINAL;
            return (
              <motion.div key={sig.id}
                className={`bg-slate-800/50 border ${s.border} rounded-xl p-3 cursor-pointer transition-all hover:bg-slate-700/40 hover:translate-x-0.5 relative overflow-hidden`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                {/* Priority bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${s.dot.replace('bg-', 'bg-')}`} />

                <div className="flex items-start gap-2 ml-1.5">
                  <div className={`text-[9px] font-bold font-mono w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${s.badge}`}>
                    #{sig.priority}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-slate-200 leading-tight mb-0.5">{sig.title}</div>
                    <div className="text-[10px] text-slate-500 leading-relaxed line-clamp-2">{sig.impact.split('.')[0]}.</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button className={`text-[9px] font-semibold px-2 py-0.5 rounded-md border ${s.badge} hover:brightness-110 transition-all`}>Quick Approve</button>
                      <span className="text-[9px] font-mono text-slate-600">{sig.policyId}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
          {queue.length === 0 && (
            <div className="text-center py-6 text-slate-600 text-xs">All signals resolved</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LIVE TICKER
// ════════════════════════════════════════════════════════════════════════════

const TICKER_EVENTS = [
  { tag: 'CRITICAL', cls: 'bg-red-500/20 text-red-300', text: 'cooling_validation · Chiller C-04 pre-failure signature · Hall B at risk · 18 min window' },
  { tag: 'WARNING',  cls: 'bg-amber-500/15 text-amber-300', text: 'ppe_compliance · Nexus Build Sector 7 · 2 workers · hard hat violation #3 logged' },
  { tag: 'NOMINAL',  cls: 'bg-emerald-500/10 text-emerald-300', text: 'power_validation · Substation load balanced · 847MW steady · Grid OTA on track' },
  { tag: 'INFO',     cls: 'bg-indigo-500/15 text-indigo-300', text: 'power_water_ota · Grid utility confirmed 200MW · April 15 delivery · 11 days early' },
  { tag: 'WARNING',  cls: 'bg-amber-500/15 text-amber-300', text: 'rack_stack · Titan Systems Row 14 · 3-day delay · penalty clause §12.4 eligible' },
  { tag: 'NOMINAL',  cls: 'bg-emerald-500/10 text-emerald-300', text: 'zone_safety · All zones clear except Sector 7 · 24 workers on-site · 0 OSHA incidents' },
  { tag: 'NOMINAL',  cls: 'bg-emerald-500/10 text-emerald-300', text: 'capital_deployment · $847M deployed · $320M pending milestone gates · Schedule intact' },
];

const LiveTicker = () => {
  const doubled = [...TICKER_EVENTS, ...TICKER_EVENTS];
  return (
    <div className="h-8 bg-slate-950 border-t border-slate-700/50 flex items-center overflow-hidden flex-shrink-0">
      <div className="px-3 text-[8px] font-bold tracking-[0.2em] text-cyan-600 uppercase border-r border-slate-700 h-full flex items-center flex-shrink-0 bg-cyan-500/5">
        LIVE
      </div>
      <div className="flex-1 overflow-hidden relative">
        <div className="flex items-center gap-6 whitespace-nowrap animate-[scroll-ticker_50s_linear_infinite]"
          style={{ animationName: 'scrollTicker' }}>
          {doubled.map((e, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-[10px] text-slate-400 font-mono">
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded font-sans ${e.cls}`}>{e.tag}</span>
              {e.text}
              <span className="text-slate-700 mx-2">///</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const Axiom = () => {
  const [lens, setLens] = useState('owners_rep');
  const [signals, setSignals] = useState(INITIAL_SIGNALS);
  const [resolvedZones, setResolvedZones] = useState(new Set());
  const [resolvedPolicies, setResolvedPolicies] = useState(new Set());
  const [showBriefing, setShowBriefing] = useState(true);
  const [showProactive, setShowProactive] = useState(false);
  const [liveMW, setLiveMW] = useState(847);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => buildCampusTopology(resolvedZones), []);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // MW oscillator
  useEffect(() => {
    const id = setInterval(() => {
      setLiveMW(v => {
        const n = v + (Math.random() - 0.5) * 2.5;
        return Math.round(Math.max(840, Math.min(856, n)));
      });
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Show proactive alert after briefing closes
  useEffect(() => {
    if (!showBriefing && signals[0]?.type === 'CRITICAL') {
      const t = setTimeout(() => setShowProactive(true), 600);
      return () => clearTimeout(t);
    }
  }, [showBriefing, signals]);

  // Rebuild topology when zones resolve
  const rebuildTopology = useCallback((newResolved) => {
    const { nodes: newNodes, edges: newEdges } = buildCampusTopology(newResolved);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [setNodes, setEdges]);

  // Approve top domino — resolve zones and promote queue
  const handleApprove = useCallback((sig) => {
    setShowProactive(false);
    const newResolved = new Set([...resolvedZones, ...(sig.resolves || [])]);
    setResolvedZones(newResolved);
    setResolvedPolicies(p => new Set([...p, sig.policyId]));
    setSignals(prev => prev.filter(s => s.id !== sig.id));
    rebuildTopology(newResolved);
  }, [resolvedZones, rebuildTopology]);

  const handleDelegate = useCallback((sig) => {
    console.log('Delegated:', sig.id);
  }, []);

  const handleSnooze = useCallback((sig) => {
    setSignals(prev => {
      const rest = prev.filter(s => s.id !== sig.id);
      return [...rest, sig]; // push to back
    });
  }, []);

  // Signal counts
  const criticalCount = signals.filter(s => s.type === 'CRITICAL').length;
  const warnCount     = signals.filter(s => s.type === 'WARNING').length;

  const sigBadge = (label, count, cls, dotCls) => (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-mono font-bold cursor-pointer transition-all hover:brightness-110 ${cls}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${dotCls} ${label === 'CRITICAL' && count > 0 ? 'animate-pulse' : ''}`} />
      {count} {label}
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full bg-slate-950 overflow-hidden font-sans">

      {/* ── TOP BAR ── */}
      <div className="h-13 flex-shrink-0 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 border-b border-slate-700/50 flex items-center gap-3 px-4 z-10">
        {/* Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg flex items-center justify-center">
            <Brain className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold tracking-[0.15em] text-white text-sm">AXIOM</span>
        </div>

        {/* Campus chip */}
        <div className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-300 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Reno-1 Campus · 1.2GW Build
        </div>

        {/* Live MW */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="font-mono font-bold text-amber-400 text-base tabular-nums">{liveMW}</span>
          <span className="text-[10px] text-slate-500 font-mono">MW LIVE</span>
        </div>

        {/* Divider */}
        <div className="flex-1" />

        {/* Stakeholder lens tabs */}
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-xl p-1">
          {Object.entries(STAKEHOLDER_LENSES).map(([key, val]) => {
            const isActive = lens === key;
            return (
              <motion.button key={key}
                onClick={() => setLens(key)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all whitespace-nowrap ${isActive ? val.activeCls : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'}`}
                whileTap={{ scale: 0.96 }}
              >
                {val.label}
              </motion.button>
            );
          })}
        </div>

        {/* Signal counters */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {sigBadge('CRITICAL', criticalCount, 'bg-red-500/12 border-red-500/30 text-red-400', 'bg-red-500')}
          {sigBadge('WARN', warnCount, 'bg-amber-500/10 border-amber-500/25 text-amber-400', 'bg-amber-500')}
          {sigBadge('OK', 14 + resolvedZones.size, 'bg-emerald-500/8 border-emerald-500/20 text-emerald-400', 'bg-emerald-500')}
        </div>

        {/* Clock */}
        <ClockDisplay />
      </div>

      {/* ── MAIN AREA ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left Panel */}
        <LeftPanel lens={lens} policyDomains={POLICY_DOMAINS} resolvedPolicies={resolvedPolicies} />

        {/* Center: Campus Map */}
        <div className="flex-1 relative bg-slate-950 overflow-hidden">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.25}
            maxZoom={1.8}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant="dots" gap={20} size={1} color="rgba(99,102,241,0.12)" />
            <Controls className="!bg-slate-800/90 !border-slate-700 !rounded-xl [&>button]:!bg-slate-800 [&>button]:!border-slate-600 [&>button]:!text-slate-300 [&>button:hover]:!bg-slate-700" />
            <MiniMap
              nodeColor={(n) => {
                if (n.type === 'axiomCloud') return '#6366f1';
                const st = n.data?.status;
                if (st === 'CRITICAL') return '#ef4444';
                if (st === 'WARNING') return '#f59e0b';
                return '#10b981';
              }}
              maskColor="rgba(2,6,23,0.85)"
              className="!bg-slate-900 !border-slate-700 !rounded-xl"
            />
            {/* Map legend */}
            <Panel position="bottom-left">
              <div className="bg-slate-900/90 border border-slate-700/60 backdrop-blur-sm rounded-xl p-3 text-[9px]">
                <div className="text-slate-500 font-bold uppercase tracking-widest mb-2">Legend</div>
                {[
                  { dot: 'bg-red-500', label: 'Critical · Action Required' },
                  { dot: 'bg-amber-500', label: 'Warning · Monitoring' },
                  { dot: 'bg-emerald-500', label: 'Nominal' },
                ].map(({ dot, label }) => (
                  <div key={label} className="flex items-center gap-2 text-slate-400 mb-1">
                    <div className={`w-2 h-2 rounded-full ${dot}`} />
                    {label}
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-slate-700/60 space-y-1">
                  {Object.entries(FLOW_COLORS).slice(0, 4).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-slate-500">
                      <div className="w-4 h-px rounded" style={{ backgroundColor: v.stroke }} />
                      {v.label}
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </ReactFlow>

          {/* Proactive alert floats over map */}
          <AnimatePresence>
            {showProactive && signals.length > 0 && (
              <ProactiveDominoAlert
                signal={signals[0]}
                onViewDetails={() => setShowProactive(false)}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Right Panel */}
        <RightPanel
          signals={signals}
          onApprove={handleApprove}
          onDelegate={handleDelegate}
          onSnooze={handleSnooze}
        />
      </div>

      {/* ── BOTTOM TICKER ── */}
      <LiveTicker />

      {/* ── SHIFT BRIEFING MODAL ── */}
      <AnimatePresence>
        {showBriefing && (
          <ShiftBriefingModal
            isOpen={showBriefing}
            onClose={() => setShowBriefing(false)}
            signals={signals}
            lens={lens}
            campusName="Reno-1 · 1.2GW"
          />
        )}
      </AnimatePresence>

      {/* Global ticker keyframe */}
      <style>{`
        @keyframes scrollTicker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .animate-\\[scroll-ticker_50s_linear_infinite\\] {
          animation: scrollTicker 50s linear infinite;
        }
      `}</style>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CLOCK DISPLAY
// ════════════════════════════════════════════════════════════════════════════

const ClockDisplay = () => {
  const [time, setTime] = useState(new Date().toTimeString().slice(0, 8));
  useEffect(() => {
    const id = setInterval(() => setTime(new Date().toTimeString().slice(0, 8)), 1000);
    return () => clearInterval(id);
  }, []);
  return <div className="font-mono text-xs text-slate-600 flex-shrink-0 tabular-nums">{time}</div>;
};

export default Axiom;
